import { Button as AntdButton, Progress } from 'antd';
import type { TableColumnsType } from 'antd';
import { IconWrapper } from '@/components/ui/icon-wrapper';
import ZipIcon from '/public/file/compress.svg';
import { StyledTable } from '@/components/antd-wrapper/table/table-styled';
import { FileItem, FileStatus } from '.';
import { useI18n } from '@/hooks/use-i18n';

const RETRY_STATUS = ['error', 'validating-error'];

interface UploadFileListProps {
  files: FileItem[];
  onDelete?: (file: FileItem) => void;
  onRetry?: (file: FileItem) => void;
}

const UploadFileList = ({ files, onDelete, onRetry }: UploadFileListProps) => {
  const { t } = useI18n(['skill', 'common']);

  const statusConfig = {
    pending: {
      text: t('common:pending'),
      color: '#00000040',
    },
    uploading: {
      text: t('common:uploading'),
      color: '#7171EE',
    },
    finished: {
      text: t('common:uploadSuccess'),
      color: '#000000BF',
    },
    error: {
      text: t('common:uploadFailed'),
      color: '#E91A1A',
    },
    validating: {
      text: t('skill:upload.validating'),
      color: '#7171EE',
    },
    'validating-error': {
      text: t('skill:upload.validatingError'),
      color: '#E91A1A',
    },
  };

  const renderStatus = (status: FileStatus) => {
    const config = statusConfig[status];
    if (!config) {
      return null;
    }
    return (
      <span
        className="text-sm text-[#000000E0] truncate"
        style={{ color: config.color }}
      >
        {config.text}
      </span>
    );
  };

  // 表格列配置
  const columns: TableColumnsType = [
    {
      title: (
        <div>{t('skill:upload.skillCount', { count: files?.length || 0 })}</div>
      ),
      dataIndex: 'file_name',
      key: 'file_name',
      render: (_: string, record: FileItem) => {
        const fileName = record?.file?.name;
        return (
          <div className="flex items-center gap-[6px] px-6">
            <IconWrapper icon={ZipIcon} size={16} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#000000E0] truncate">
                {fileName}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: t('common:details'),
      dataIndex: 'status',
      key: 'status',
      width: 220,
      render: (_: string, record: FileItem) => (
        <div className="h-full flex items-center px-4">
          {renderStatus(record.status)}
        </div>
      ),
    },
    {
      title: t('common:actions'),
      key: 'actions',
      width: 124,
      align: 'right',
      render: (_: string, record: FileItem) => (
        <div className="flex items-center justify-end gap-6 px-4">
          {RETRY_STATUS.includes(record.status) && (
            <AntdButton
              type="link"
              onClick={() => onRetry(record)}
              className="text-[#7171EE] hover:!text-[#7171EE] h-8 !p-0"
            >
              {t('common:retry')}
            </AntdButton>
          )}
          <AntdButton
            type="link"
            onClick={() => onDelete(record)}
            className="text-[#7171EE] hover:!text-[#7171EE] h-8 !p-0"
          >
            {t('common:delete')}
          </AntdButton>
        </div>
      ),
    },
  ];

  return (
    <StyledTable
      rowKey="id"
      columns={columns}
      dataSource={files}
      pagination={false}
      tableHeight="auto"
    />
  );
};

export default UploadFileList;
